import { z } from 'zod';
import { ColumnType } from './columns';

const WorkspaceFields = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(2000).optional(),
  color: z.string().regex(/^#[0-9a-f]{6}$/i).optional(),
  icon: z.string().max(50).optional(),
});

/**
 * A workspace always goes into an organization. Placing it in one of the
 * organization's folders, or giving it to someone else to own, is for
 * whoever manages organizations; anyone else who creates workspaces owns
 * theirs, at the organization's top level.
 */
export const CreateWorkspace = WorkspaceFields.extend({
  organizationId: z.string().uuid(),
  folderId: z.string().uuid().nullable().optional(),
  ownerId: z.string().uuid().optional(),
});

/**
 * Rename, recolour, or open and close it. Open: any staff member finds it,
 * looks around and joins with one click. Closed: invite-only.
 */
export const UpdateWorkspace = WorkspaceFields.partial().extend({ isOpen: z.boolean().optional() });

/** Where a workspace goes: another organization, or another of its folders. Without a folder, the top level. */
export const MoveWorkspace = z.object({
  organizationId: z.string().uuid(),
  folderId: z.string().uuid().nullable().optional(),
});

/**
 * The top of the hierarchy: organization, its folders, workspaces, boards.
 * It also holds docs, dashboards and workflows of its own.
 */
export const CreateOrganization = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(2000).optional(),
  color: z.string().regex(/^#[0-9a-f]{6}$/i).optional(),
  icon: z.string().max(50).optional(),
});

/** Rename, recolour, archive or bring back. */
export const UpdateOrganization = CreateOrganization.partial().extend({
  description: z.string().trim().max(2000).nullable().optional(),
  isArchived: z.boolean().optional(),
});

/**
 * Docs, dashboards, workflows and folders live in exactly one place: a
 * workspace or an organization. Ones made before organizations existed keep
 * their workspace.
 */
export const containerFields = {
  workspaceId: z.string().uuid().optional(),
  organizationId: z.string().uuid().optional(),
};

export function oneContainer(value: { workspaceId?: string; organizationId?: string }, ctx: z.RefinementCtx) {
  if (Boolean(value.workspaceId) === Boolean(value.organizationId)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['organizationId'], message: 'validation.one_container' });
  }
}

/** Swatches offered when creating a workspace. */
export const WORKSPACE_COLORS = [
  '#1B4FA8', '#E8112D', '#1F9D6C', '#F2A93B', '#7B61FF', '#12275B',
] as const;

/**
 * Adding someone or changing their role. Never to or from owner: a workspace
 * changes hands only through TransferWorkspace, which its owner starts.
 */
export const AddMember = z.object({
  userId: z.string().uuid(),
  role: z.enum(['admin', 'member', 'viewer']).default('member'),
});

/** "Make owner": the member who takes the workspace over. The old owner stays on as an admin. */
export const TransferWorkspace = z.object({ userId: z.string().uuid() });

/**
 * Everyone in a department joins with this role; people already in keep
 * theirs. Not as owners - handing a workspace to a whole department at once
 * is never what was meant.
 */
export const AddDepartmentMembers = z.object({
  departmentId: z.string().uuid(),
  role: z.enum(['admin', 'member', 'viewer']).default('member'),
});

export const CreateBoard = z.object({
  workspaceId: z.string().uuid(),
  name: z.string().min(1).max(120),
  description: z.string().max(2000).optional(),
  folderId: z.string().uuid().nullable().optional(),
  isPrivate: z.boolean().default(false),
  /** A built-in template's key or a saved template's id; without one, the blank board. */
  templateId: z.string().min(1).max(64).optional(),
});

/** The Template center's categories, in the order its chips show them. */
export const templateCategories = [
  'project_management', 'software', 'product', 'marketing', 'sales', 'hr', 'operations', 'general',
] as const;
export const TemplateCategory = z.enum(templateCategories);
export type TemplateCategory = z.infer<typeof TemplateCategory>;

/** A saved template carries at most this many sample tasks. */
export const TEMPLATE_MAX_ITEMS = 200;

/**
 * Save a board as a template. Without a category it shows under team
 * templates; with items it keeps its tasks' names and values, never their
 * people, files, updates or subitems.
 */
export const SaveTemplate = z.object({
  boardId: z.string().uuid(),
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(500).optional(),
  category: TemplateCategory.nullable().optional(),
  includeItems: z.boolean().default(false),
});

export const boardPermissions = ['edit_everything', 'edit_content', 'edit_own', 'view_comment'] as const;
export const BoardPermission = z.enum(boardPermissions);
export type BoardPermission = z.infer<typeof BoardPermission>;

/** The level that applies to one person: full rights, or the board's level. */
export type BoardLevel = 'owner' | BoardPermission;

/** What the board page may offer the person looking at it. */
export interface BoardAbilities {
  level: BoardLevel;
  isOwner: boolean;
  /** Columns, groups, labels, imports, automations and board settings. */
  editStructure: boolean;
  /** Every item on the board. */
  editItems: boolean;
  /** Only items they created or are assigned to. */
  editOwnItems: boolean;
  createItems: boolean;
  /** Turn privacy on or off, and choose who is invited. The creator only. */
  managePrivacy: boolean;
  /** Move it to another workspace: its creator or a workspace admin; a private board, its creator. */
  moveBoard: boolean;
  /** Hand it to another member of the workspace. The creator only. */
  handOver: boolean;
  /** Looking around an open workspace without having joined it: view and comment until they join. */
  isVisitor: boolean;
}

/** Where a board goes when it changes workspace. Without a folder it lands at the top level. */
export const MoveBoard = z.object({
  workspaceId: z.string().uuid(),
  folderId: z.string().uuid().nullable().optional(),
});

/** The creator hands the board to another active member of its workspace. */
export const TransferBoard = z.object({ userId: z.string().uuid() });

/** Owners and the level are set by owners; privacy and invites by the creator. */
export const UpdateBoardAccess = z.object({
  permission: BoardPermission.optional(),
  ownerIds: z.array(z.string().uuid()).max(100).optional(),
  isPrivate: z.boolean().optional(),
  memberIds: z.array(z.string().uuid()).max(500).optional(),
});

export const projectHealths = ['on_track', 'at_risk', 'off_track'] as const;
export const ProjectHealth = z.enum(projectHealths);
export type ProjectHealth = z.infer<typeof ProjectHealth>;

/**
 * Make a board a project, or stop; set its health by hand, or clear it
 * (null) to go back to the calculated one.
 */
export const UpdateProject = z.object({
  isProject: z.boolean().optional(),
  healthOverride: ProjectHealth.nullable().optional(),
});

/** Why a project has the health it has. Codes, so each language words them. */
export type ProjectHealthReason =
  | { code: 'no_items' }
  | { code: 'all_done' }
  | { code: 'on_pace' }
  | { code: 'timeline_ended' }
  | { code: 'overdue'; overdue: number; open: number }
  | { code: 'behind'; points: number };

type PersonRef = { id: string; name: string; avatarUrl: string | null };

/** GET /boards/:id/overview - one project's progress, health and workload. */
export interface ProjectOverview {
  isProject: boolean;
  /** The override when an owner set one, otherwise the calculated health. */
  health: ProjectHealth;
  healthOverride: ProjectHealth | null;
  calculatedHealth: ProjectHealth;
  healthReasons: ProjectHealthReason[];
  items: { total: number; done: number; open: number; progress: number };
  timeline: {
    start: string | null; end: string | null;
    daysLeft: number | null; expectedProgress: number | null;
  };
  overdue: { count: number; items: { id: string; name: string; due: string; daysLate: number }[] };
  /** The main status column's labels in order, then "no status" (label null). */
  statusBreakdown: { label: string | null; color: string; isDone: boolean; count: number }[];
  effort: { column: string; total: number; done: number } | null;
  budget: { column: string; total: number; done: number } | null;
  timeLoggedSeconds: number;
  workload: (PersonRef & { open: number; done: number; effort: number; openEffort: number })[];
  recentUpdates: {
    id: string; excerpt: string; createdAt: string;
    item: { id: string; name: string };
    user: PersonRef;
  }[];
}

/** Folders nest this many levels deep at most, as in monday.com. */
export const FOLDER_MAX_DEPTH = 3;

export const CreateFolder = z.object({
  ...containerFields,
  name: z.string().trim().min(1).max(120),
  parentId: z.string().uuid().nullable().optional(),
}).superRefine(oneContainer);

export const UpdateFolder = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  parentId: z.string().uuid().nullable().optional(),
});

/** A doc's body is saved whole on every autosave, so it has a ceiling. */
export const DOC_MAX_LENGTH = 500_000;

export const CreateDoc = z.object({
  ...containerFields,
  folderId: z.string().uuid().nullable().optional(),
  name: z.string().trim().min(1).max(255),
  content: z.string().max(DOC_MAX_LENGTH).optional(),
  isPrivate: z.boolean().default(false),
}).superRefine(oneContainer);

/** Rename, move, save the body or change privacy - any of them, in one call. */
export const UpdateDoc = z.object({
  name: z.string().trim().min(1).max(255).optional(),
  folderId: z.string().uuid().nullable().optional(),
  content: z.string().max(DOC_MAX_LENGTH).nullable().optional(),
  isPrivate: z.boolean().optional(),
});

export const DocMembers = z.object({
  userIds: z.array(z.string().uuid()).max(500),
});

export const TransferDoc = z.object({ userId: z.string().uuid() });

/** Lines of a doc that become items, one each, in a group of a board. */
export const CreateItemsFromDoc = z.object({
  groupId: z.string().uuid(),
  lines: z.array(z.string().max(500)).min(1).max(50),
});

export const CreateDocComment = z.object({
  content: z.string().trim().min(1).max(20_000),
  /** The text the thread is about. Replies carry none. */
  quote: z.string().max(2000).nullable().optional(),
  parentId: z.string().uuid().nullable().optional(),
  mentions: z.array(z.string().uuid()).max(50).default([]),
});

export const ResolveDocComment = z.object({ resolved: z.boolean() });

/** What the doc page may offer the person looking at it. */
export interface DocAbilities {
  /** Write the body, rename, move, delete. Workspace viewers only read and comment. */
  edit: boolean;
  /** Privacy, who is invited, handing the doc over. The creator only. */
  manage: boolean;
}

export const CreateColumn = z.object({
  boardId: z.string().uuid(),
  name: z.string().min(1).max(80),
  type: ColumnType,
  settings: z.record(z.unknown()).optional(),
  isRequired: z.boolean().default(false),
});

export const CreateItem = z.object({
  groupId: z.string().uuid(),
  name: z.string().min(1).max(500),
  description: z.string().max(10_000).optional(),
  parentItemId: z.string().uuid().optional(),
  values: z.record(z.unknown()).default({}),
});

export const UpdateItemValues = z.object({
  values: z.record(z.unknown()),
});

export const StartTimer = z.object({ itemId: z.string().uuid() });

export type CreateWorkspace = z.infer<typeof CreateWorkspace>;
export type UpdateWorkspace = z.infer<typeof UpdateWorkspace>;
export type MoveWorkspace = z.infer<typeof MoveWorkspace>;
export type CreateOrganization = z.infer<typeof CreateOrganization>;
export type UpdateOrganization = z.infer<typeof UpdateOrganization>;
export type CreateBoard = z.infer<typeof CreateBoard>;
export type UpdateBoardAccess = z.infer<typeof UpdateBoardAccess>;
export type UpdateProject = z.infer<typeof UpdateProject>;
export type MoveBoard = z.infer<typeof MoveBoard>;
export type SaveTemplate = z.infer<typeof SaveTemplate>;
export type CreateFolder = z.infer<typeof CreateFolder>;
export type UpdateFolder = z.infer<typeof UpdateFolder>;
export type CreateItem = z.infer<typeof CreateItem>;
export type CreateDoc = z.infer<typeof CreateDoc>;
export type UpdateDoc = z.infer<typeof UpdateDoc>;
export type CreateItemsFromDoc = z.infer<typeof CreateItemsFromDoc>;
export type CreateDocComment = z.infer<typeof CreateDocComment>;

// Bug reports and browser-side failures on PM pages.

export const clientErrorKinds = ['render_error', 'unhandled_rejection', 'window_error', 'network_error'] as const;
export type ClientErrorKind = (typeof clientErrorKinds)[number];

/**
 * What a browser may send about one failure. Bounded everywhere: any signed-in
 * PM user can post here, so a runaway error loop must not be able to write
 * unbounded rows. The page trims to these limits before sending.
 */
export const CLIENT_ERROR_LIMITS = {
  message: 1000, stack: 4000, component: 255, url: 500, requestUrl: 500,
} as const;

export const ClientErrorReport = z.object({
  kind: z.enum(clientErrorKinds),
  message: z.string().min(1).max(CLIENT_ERROR_LIMITS.message),
  stack: z.string().max(CLIENT_ERROR_LIMITS.stack).optional(),
  component: z.string().max(CLIENT_ERROR_LIMITS.component).optional(),
  url: z.string().max(CLIENT_ERROR_LIMITS.url).optional(),
  requestUrl: z.string().max(CLIENT_ERROR_LIMITS.requestUrl).optional(),
  requestMethod: z.string().max(10).optional(),
  boardId: z.string().uuid().optional(),
  workspaceId: z.string().uuid().optional(),
  viewport: z.string().max(20).optional(),
});

export const CreateBugReport = z.object({
  title: z.string().trim().min(1).max(255),
  description: z.string().trim().min(1).max(5000),
  url: z.string().max(500).optional(),
  boardId: z.string().uuid().optional(),
  workspaceId: z.string().uuid().optional(),
  /** An image the reporter uploaded through /uploads. */
  screenshotId: z.string().uuid().optional(),
});

export type ClientErrorReport = z.infer<typeof ClientErrorReport>;
export type CreateBugReport = z.infer<typeof CreateBugReport>;
// ── dashboards ─────────────────────────────────────────────────────────────

/** Boards one dashboard reads from at most, as monday's Pro plan; it keeps the data query bounded. */
export const DASHBOARD_MAX_BOARDS = 20;

export const dashboardWidgetTypes = ['numbers', 'chart', 'battery', 'table', 'timeline'] as const;
export const DashboardWidgetType = z.enum(dashboardWidgetTypes);
export type DashboardWidgetType = z.infer<typeof DashboardWidgetType>;

/**
 * A widget's own narrowing, on top of the dashboard's: only some boards,
 * only items assigned to some people, only items whose column holds one of
 * some values. It can only take away from what the viewer already sees.
 */
export const WidgetFilters = z.object({
  boardIds: z.array(z.string().uuid()).max(50).optional(),
  peopleIds: z.array(z.string().uuid()).max(50).optional(),
  column: z.string().max(255).nullable().optional(),
  values: z.array(z.string().max(255)).max(30).optional(),
});
export type WidgetFilters = z.infer<typeof WidgetFilters>;

/** Columns are named, not referenced: "Status" on every connected board is one column. */
export const WidgetConfig = z.object({
  column: z.string().max(255).nullable().optional(),
  groupBy: z.enum(['column', 'person', 'group', 'board']).optional(),
  style: z.enum(['donut', 'pie', 'bar']).optional(),
  fn: z.enum(['count', 'sum', 'avg', 'min', 'max']).optional(),
  columns: z.array(z.string().max(255)).max(6).optional(),
  filters: WidgetFilters.optional(),
});
export type WidgetConfig = z.infer<typeof WidgetConfig>;

export const CreateDashboard = z.object({
  ...containerFields,
  folderId: z.string().uuid().nullable().optional(),
  name: z.string().trim().min(1).max(255),
  isPrivate: z.boolean().default(false),
  boardIds: z.array(z.string().uuid()).max(DASHBOARD_MAX_BOARDS).default([]),
}).superRefine(oneContainer);

/** Rename, move, connect boards or change privacy - any of them, in one call. */
export const UpdateDashboard = z.object({
  name: z.string().trim().min(1).max(255).optional(),
  folderId: z.string().uuid().nullable().optional(),
  isPrivate: z.boolean().optional(),
  boardIds: z.array(z.string().uuid()).max(DASHBOARD_MAX_BOARDS).optional(),
});

export const DashboardMembers = z.object({
  userIds: z.array(z.string().uuid()).max(500),
});

export const CreateDashboardWidget = z.object({
  type: DashboardWidgetType,
  title: z.string().trim().min(1).max(255),
  width: z.number().int().min(1).max(3).optional(),
  config: WidgetConfig.default({}),
});

export const UpdateDashboardWidget = z.object({
  title: z.string().trim().min(1).max(255).optional(),
  width: z.number().int().min(1).max(3).optional(),
  config: WidgetConfig.optional(),
});

export const ReorderDashboardWidgets = z.object({
  widgetIds: z.array(z.string().uuid()).max(200),
});

/** What the dashboard page may offer the person looking at it. */
export interface DashboardAbilities {
  /** Layout, widgets, connected boards, name, folder, delete. */
  edit: boolean;
  /** Privacy and who is invited. The creator only. */
  manage: boolean;
}

/** One value as a widget sees it; label columns carry their colour and whether it is a done state. */
export interface DashboardCell {
  type: ColumnType;
  value: string | number | boolean | string[] | { start: string; end: string } | null;
  color?: string | null;
  done?: boolean;
  /** A dropdown's colour for each of its labels. */
  colors?: (string | null)[];
}

/** A slice of a chart or battery. A null label is "no value". */
export interface DashboardSeries {
  key: string;
  label: string | null;
  color: string | null;
  count: number;
}

export type DashboardWidgetResult =
  | { type: 'numbers'; value: number | null; counted?: number }
  | { type: 'chart'; series: DashboardSeries[] }
  | { type: 'battery'; series: DashboardSeries[]; done: number; total: number; percent: number }
  | {
      type: 'table';
      total: number;
      rows: {
        id: string; name: string; boardId: string; board: string;
        group: { name: string; color: string | null };
        people: string[];
        cells: (DashboardCell | null)[];
      }[];
    }
  | {
      type: 'timeline';
      total: number;
      bars: { id: string; boardId: string; name: string; board: string; color: string; start: string; end: string }[];
    };

/** GET /dashboards/:id/data - every widget, worked out for the person asking. */
export interface DashboardData {
  /** Connected boards; one the viewer cannot open shows no name. */
  boards: { id: string; name: string | null; accessible: boolean }[];
  /** Column names on the boards they can open, for picking and filtering. */
  columns: { name: string; types: ColumnType[]; labels: string[] }[];
  /** People assigned to the items they can see, for the person filter. */
  people: { id: string; name: string; avatarUrl: string | null }[];
  itemCount: number;
  /** More items than a dashboard reads; the numbers cover the first ones. */
  truncated: boolean;
  widgets: Record<string, DashboardWidgetResult>;
}

// ── forms ──────────────────────────────────────────────────────────────────

/** Column types a stranger can fill in. People, files and the like need an account. */
export const FORM_FIELD_TYPES = [
  'text', 'number', 'date', 'link', 'email', 'phone', 'status', 'priority', 'dropdown', 'checkbox', 'rating',
] as const satisfies readonly ColumnType[];

export const FormQuestion = z.object({
  columnId: z.string().uuid(),
  /** Null asks with the column's own name. */
  label: z.string().trim().max(255).nullable().optional(),
  required: z.boolean().default(false),
  /** A text question answered in a box of several lines. */
  long: z.boolean().default(false),
});
export type FormQuestion = z.infer<typeof FormQuestion>;

export const CreateForm = z.object({
  title: z.string().trim().min(1).max(255),
  description: z.string().trim().max(2000).nullable().optional(),
  nameLabel: z.string().trim().max(255).nullable().optional(),
  groupId: z.string().uuid().nullable().optional(),
  isActive: z.boolean().default(true),
  fields: z.array(FormQuestion).max(30).default([]),
});

export const UpdateForm = z.object({
  title: z.string().trim().min(1).max(255).optional(),
  description: z.string().trim().max(2000).nullable().optional(),
  nameLabel: z.string().trim().max(255).nullable().optional(),
  groupId: z.string().uuid().nullable().optional(),
  isActive: z.boolean().optional(),
  fields: z.array(FormQuestion).max(30).optional(),
});

/**
 * What a stranger sends. Loose on purpose: the answers are checked against
 * the board's columns as they are now, and `website` is a honeypot.
 */
export const SubmitForm = z.object({
  name: z.unknown().optional(),
  answers: z.record(z.unknown()).default({}),
  website: z.unknown().optional(),
});

/** GET /public/forms/:token - the form's own words and questions, nothing about the board. */
export interface PublicForm {
  title: string;
  description: string | null;
  /** Null asks with the default wording. */
  nameLabel: string | null;
  questions: {
    columnId: string;
    label: string;
    required: boolean;
    type: (typeof FORM_FIELD_TYPES)[number];
    long: boolean;
    /** Status, priority and dropdown labels. */
    options: string[];
    /** A dropdown that takes several labels. */
    multiple: boolean;
  }[];
}

export type CreateDashboard = z.infer<typeof CreateDashboard>;
export type UpdateDashboard = z.infer<typeof UpdateDashboard>;
export type CreateDashboardWidget = z.infer<typeof CreateDashboardWidget>;
export type UpdateDashboardWidget = z.infer<typeof UpdateDashboardWidget>;
export type CreateForm = z.infer<typeof CreateForm>;
export type UpdateForm = z.infer<typeof UpdateForm>;
export type SubmitForm = z.infer<typeof SubmitForm>;
